import { useRef, useState } from "react";
import { Card, Stack, Typography, Alert } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import moment, { Moment } from "moment";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import financeService, { ImportBnsfFuelRatesResult } from "services/financeService";

// Heidi's files are named like "BNSF Fuel 4.1.26.xlsx" — pull the effective date out of that.
const parseDateFromFileName = (fileName: string): Moment | null => {
  const match = fileName.match(/(\d{1,2})[.\-_/](\d{1,2})[.\-_/](\d{2,4})/);
  if (!match) return null;
  const [, m, d, y] = match;
  const parsed = moment(`${m}/${d}/${y}`, ["M/D/YY", "M/D/YYYY"], true);
  return parsed.isValid() ? parsed : null;
};

const BnsfFuelRateImport = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [effectiveDate, setEffectiveDate] = useState<Moment | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<ImportBnsfFuelRatesResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelected = (files: FileList | null) => {
    setResult(null);
    setError(null);
    if (!files || files.length === 0) return;
    const selected = files[0];
    setFile(selected);
    // Default the effective date from the filename if we can, but let the user override.
    const fromName = parseDateFromFileName(selected.name);
    if (fromName) setEffectiveDate(fromName);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!file || !effectiveDate) return;
    setIsUploading(true);
    setResult(null);
    setError(null);
    try {
      const response = await financeService.importBnsfFuelRates(file, effectiveDate.format("YYYY-MM-DD"));
      setResult(response);
      setFile(null);
    } catch (e) {
      setError("Upload failed. Check the file and try again, or contact support if it persists.");
    } finally {
      setIsUploading(false);
    }
  };

  const canUpload = !!file && !!effectiveDate && effectiveDate.isValid() && !isUploading;

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        BNSF Fuel Rate Import
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Upload the BNSF fuel rate spreadsheet (.xlsx) and choose the effective date. The
        rates are applied to each hub automatically.
      </Typography>

      <Stack spacing={2} sx={{ maxWidth: 420 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          style={{ display: "none" }}
          onChange={(e) => handleFileSelected(e.target.files)}
        />
        <MDBox display="flex" alignItems="center" gap={2}>
          <MDButton variant="contained" color="info" onClick={() => fileInputRef.current?.click()}>
            Choose File
          </MDButton>
          <Typography variant="body2" color={file ? "text.primary" : "text.secondary"}>
            {file ? file.name : "No file selected"}
          </Typography>
        </MDBox>

        <DatePicker
          label="Effective Date"
          value={effectiveDate}
          onChange={(value) => setEffectiveDate(value)}
          slotProps={{ textField: { fullWidth: true } }}
        />

        <MDButton variant="contained" color="success" disabled={!canUpload} onClick={handleUpload}>
          {isUploading ? "Uploading…" : "Upload & Apply Rates"}
        </MDButton>

        {result && (
          <Alert severity="success">
            Imported {result.insertedCount} fuel rate
            {result.insertedCount === 1 ? "" : "s"} from {result.parsedRowCount} row
            {result.parsedRowCount === 1 ? "" : "s"} in the spreadsheet.
          </Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </Card>
  );
};

export default BnsfFuelRateImport;
